import { CSSProperties, FC, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import DropPlaceholder from './DropPlaceholder';
import {
    IndicatorItemMeasurement,
    IndicatorRect,
    InsertionIndicatorLayout,
    resolveInsertionIndicatorLayout,
} from './insertionIndicator';
import { resolveSortableInsertionIndicatorZIndex } from './sortableInsertionIndicatorZIndex';

interface SortableInsertionIndicatorProps {
    destinationIndex: number;
    direction: 'horizontal' | 'vertical';
    draggableId: string;
    droppableId: string;
}

interface IndicatorViewport {
    bottom: number;
    height: number;
    left: number;
    right: number;
    top: number;
    width: number;
    zIndex: number;
}

const clipsOverflow = (value: string): boolean => /(auto|clip|hidden|scroll)/u.test(value);

const measureSurfaceZIndex = (container: HTMLElement): number => {
    let highestZIndex = 0;
    let element: HTMLElement | null = container;

    while (element) {
        const zIndex = Number.parseInt(getComputedStyle(element).zIndex, 10);

        if (Number.isFinite(zIndex)) {
            highestZIndex = Math.max(highestZIndex, zIndex);
        }

        element = element.parentElement;
    }

    return highestZIndex;
};

const measureIndicatorViewport = (container: HTMLElement): IndicatorViewport => {
    let left = 0;
    let right = window.innerWidth;
    let top = 0;
    let bottom = window.innerHeight;
    let ancestor = container.parentElement;

    while (ancestor) {
        const style = getComputedStyle(ancestor);
        const rect = ancestor.getBoundingClientRect();

        if (clipsOverflow(style.overflowX)) {
            left = Math.max(left, rect.left);
            right = Math.min(right, rect.right);
        }

        if (clipsOverflow(style.overflowY)) {
            top = Math.max(top, rect.top);
            bottom = Math.min(bottom, rect.bottom);
        }

        ancestor = ancestor.parentElement;
    }

    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);

    return { bottom, height, left, right, top, width, zIndex: measureSurfaceZIndex(container) };
};

const toRect = (rect: DOMRect): IndicatorRect => ({
    bottom: rect.bottom,
    height: rect.height,
    left: rect.left,
    right: rect.right,
    top: rect.top,
    width: rect.width,
});

const measureItem = (element: HTMLElement): IndicatorItemMeasurement => {
    const style = getComputedStyle(element);

    return {
        marginBottom: Number.parseFloat(style.marginBottom) || 0,
        marginLeft: Number.parseFloat(style.marginLeft) || 0,
        marginRight: Number.parseFloat(style.marginRight) || 0,
        marginTop: Number.parseFloat(style.marginTop) || 0,
        rect: toRect(element.getBoundingClientRect()),
    };
};

const SortableInsertionIndicator: FC<SortableInsertionIndicatorProps> = ({
    destinationIndex,
    direction,
    draggableId,
    droppableId,
}) => {
    const { token } = useAntDesignTheme();
    const [layout, setLayout] = useState<InsertionIndicatorLayout>();
    const [viewport, setViewport] = useState<IndicatorViewport>();
    const frameRef = useRef<number>();

    const updateLayout = useCallback(() => {
        const container = document.querySelector<HTMLElement>(`[data-rfd-droppable-id="${CSS.escape(droppableId)}"]`);
        const dragged = document.querySelector<HTMLElement>(`[data-rfd-draggable-id="${CSS.escape(draggableId)}"]`);

        if (!container || !dragged) {
            setLayout(undefined);
            setViewport(undefined);
            return;
        }

        const items = Array.from(container.querySelectorAll<HTMLElement>('[data-rfd-draggable-id]')).filter(
            (element) =>
                element.dataset.rfdDraggableId !== draggableId &&
                element.closest('[data-rfd-droppable-id]') === container,
        );
        const containerStyle = getComputedStyle(container);
        const gap =
            Number.parseFloat(direction === 'horizontal' ? containerStyle.columnGap : containerStyle.rowGap) || 0;
        const paddingStart =
            Number.parseFloat(direction === 'horizontal' ? containerStyle.paddingLeft : containerStyle.paddingTop) || 0;

        setLayout(
            resolveInsertionIndicatorLayout({
                containerRect: toRect(container.getBoundingClientRect()),
                destinationIndex,
                direction,
                dragged: measureItem(dragged),
                gap,
                items: items.map(measureItem),
                paddingStart,
            }),
        );
        setViewport(measureIndicatorViewport(container));
    }, [destinationIndex, direction, draggableId, droppableId]);

    useLayoutEffect(() => {
        const scheduleLayout = () => {
            window.cancelAnimationFrame(frameRef.current ?? 0);
            frameRef.current = window.requestAnimationFrame(updateLayout);
        };

        const container = document.querySelector<HTMLElement>(`[data-rfd-droppable-id="${CSS.escape(droppableId)}"]`);
        const observer = container ? new MutationObserver(scheduleLayout) : undefined;

        if (container) {
            observer?.observe(container, {
                attributeFilter: ['style'],
                attributes: true,
                childList: true,
                subtree: true,
            });
        }

        scheduleLayout();
        window.addEventListener('resize', scheduleLayout);
        document.addEventListener('scroll', scheduleLayout, true);

        return () => {
            observer?.disconnect();
            window.cancelAnimationFrame(frameRef.current ?? 0);
            window.removeEventListener('resize', scheduleLayout);
            document.removeEventListener('scroll', scheduleLayout, true);
        };
    }, [droppableId, updateLayout]);

    if (!layout || !viewport || viewport.height === 0 || viewport.width === 0) {
        return null;
    }

    const viewportStyle: CSSProperties = {
        height: viewport.height,
        left: viewport.left,
        overflow: 'hidden',
        pointerEvents: 'none',
        position: 'fixed',
        top: viewport.top,
        width: viewport.width,
        zIndex: resolveSortableInsertionIndicatorZIndex(token.zIndexPopupBase, viewport.zIndex),
    };
    const width = Math.min(layout.width, viewport.width);
    const height = Math.min(layout.height, viewport.height);
    const left = Math.min(Math.max(layout.left, viewport.left), Math.max(viewport.left, viewport.right - width));
    const top = Math.min(Math.max(layout.top, viewport.top), Math.max(viewport.top, viewport.bottom - height));
    const placeholderStyle: CSSProperties = {
        height,
        left: left - viewport.left,
        position: 'absolute',
        top: top - viewport.top,
        transition: `inset ${token.motionDurationFast} ${token.motionEaseInOut}, width ${token.motionDurationFast} ${token.motionEaseInOut}, height ${token.motionDurationFast} ${token.motionEaseInOut}`,
        width,
    };

    return createPortal(
        <div data-platform-sortable-insertion-viewport='true' style={viewportStyle}>
            <DropPlaceholder data-platform-sortable-insertion-indicator='true' style={placeholderStyle} />
        </div>,
        document.body,
    );
};

export default SortableInsertionIndicator;
